<script setup>
import PurchaseHistoryCard from './PurchaseHistoryCard.vue';
import { formatNamingPurchaseLabel } from './namingPurchaseLabel';
import { useNamingPurchaseHistory } from '@/composables/saju-insight/useNamingPurchaseHistory';
import { addContentLogAPI } from '@/api/logServiceApi';

const props = defineProps({
  guestUserId: { type: [String, Number], default: null },
  /** 'cover' (overview에서) | 'detail' (detail 진입 후) */
  mode: {
    type: String,
    default: 'cover',
  },
  /** cover 모드에서 시작 가능 여부 */
  ready: {
    type: Boolean,
    default: false,
  },
  /** 보유 작명분석권 수량 */
  ticketCount: {
    type: Number,
    default: 0,
  },
  /** 현재 보고 있는 결과의 analysisLogId — 해당 구매내역 카드 활성 + 카테고리 자동 전환 */
  activeLogId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['start', 'reset', 'select-history']);

/** 구매 내역 데이터/필터는 PC/모바일이 공유하는 composable에서 가져온다. */
const {
  list,
  filterByCategory,
  categoryPurchaseMap,
  fetchAll: fetchAllPurchaseHistory,
  PURCHASE_CATEGORY_TABS: categories,
} = useNamingPurchaseHistory(() => props.guestUserId);
const activeCategory = ref(categories[0].key);
const filteredPurchaseList = computed(() => filterByCategory(activeCategory.value));

/** 방금 구매/선택한 항목(activeLogId)의 카테고리로 탭 자동 전환.
 *  결제 직후 list 갱신 타이밍을 위해 activeLogId·list 둘 다 watch. */
watch(
  [() => props.activeLogId, list],
  ([id, currentList]) => {
    if (!id || !currentList?.length) return;
    const item = currentList.find((it) => String(it.analysisLogId) === String(id));
    const cat = item?.modeKey?.split('-')[1];
    if (cat && categoryPurchaseMap.value[cat]) activeCategory.value = cat;
  },
  { immediate: true },
);

/** 구매 내역 목록 스크롤 컨테이너 — 카테고리 전환 시 맨 위로 되돌리기 위해 참조 */
const historyListRef = ref(null);

const onClickCategoryTab = (key) => {
  if (!categoryPurchaseMap.value[key]) return;
  addContentLogAPI({
    contentType: 'ap_naming_tab_category_opt3',
    option3: categories.find((c) => c.key === key)?.label || key,
  });
  activeCategory.value = key;
  // 카테고리 전환 시 목록 스크롤을 맨 위로 초기화 (이전 카테고리 스크롤 위치 잔존 방지)
  nextTick(() => {
    if (historyListRef.value) historyListRef.value.scrollTop = 0;
  });
};

onMounted(async () => {
  await fetchAllPurchaseHistory();
  // 활성 카테고리 결정 — activeLogId watch와 동일 우선순위로 맞춰 로그와 실제 표시를 일치시킨다.
  //  ① 보고 있는/방금 결제한 항목(activeLogId)의 카테고리 ② 없으면 데이터 있는 첫 카테고리
  const activeItem = props.activeLogId != null
    ? list.value.find((it) => String(it.analysisLogId) === String(props.activeLogId))
    : null;
  const activeCat = activeItem?.modeKey?.split('-')[1];
  if (activeCat && categoryPurchaseMap.value[activeCat]) {
    activeCategory.value = activeCat;
  } else {
    // 데이터 있는 첫 카테고리로 reset
    const firstAvailable = categories.find((t) => categoryPurchaseMap.value[t.key]);
    if (firstAvailable) activeCategory.value = firstAvailable.key;
  }
  // 패널 첫 노출 시 자동 활성화된 카테고리도 탭 선택(조회)으로 1회 기록
  if (categoryPurchaseMap.value[activeCategory.value]) {
    addContentLogAPI({
      contentType: 'ap_naming_tab_category_opt3',
      option3: categories.find((c) => c.key === activeCategory.value)?.label || activeCategory.value,
    });
  }
});

const onClickPurchaseCard = (item) => {
  addContentLogAPI({
    contentType: 'ap_naming_log_card_open_opt3',
    option3: `${item?.modeKey ?? ''},${item?.analysisLogId ?? ''}`,
  });
  emit('select-history', item);
};

const isDetailMode = computed(() => props.mode === 'detail');
const isCtaDisabled = computed(() => !isDetailMode.value && !props.ready);

const onClickCta = () => {
  if (isCtaDisabled.value) return;
  if (isDetailMode.value) {
    emit('reset');
  } else {
    emit('start');
  }
};
</script>

<template>
  <div class="naming-panel">
    <div class="naming-panel__ticket">
      <span class="naming-panel__ticket-label">
        <i class="ticket-icon__analysis"/>
        보유 작명분석권
      </span>
      <span v-if="ticketCount > 0" class="naming-panel__ticket-count">{{ ticketCount }}건</span>
      <span v-else class="naming-panel__ticket-empty">보유한 분석권이 없습니다.</span>
    </div>

    <div class="naming-panel__hero">
      <p class="naming-panel__hero-title">오래 불릴 이름에는 이유가 있으니까</p>
      <div class="naming-panel__hero-body">
        <p>이름은 단순한 호칭을 넘어, 마음과 의미를 담아 오래 함께하는 존재가 됩니다.</p>
        <p>
          낭만작명은 사주의 흐름과 원하는 분위기를 바탕으로 사람과 반려 존재, 브랜드와 아호까지
          자연스럽게 어울리는 이름을 함께 찾아갑니다.
        </p>
        <p>오래도록 편안하게 불리고, 시간이 지나도 의미 있게 남을 이름을 만나보세요.</p>
      </div>
    </div>

    <div class="naming-panel__history">
      <div class="naming-panel__history-title">구매한 내역</div>
      <div class="naming-panel__tabs">
        <button
          v-for="c in categories"
          :key="c.key"
          type="button"
          class="naming-panel__tab"
          :class="{
            active: activeCategory === c.key && categoryPurchaseMap[c.key],
            'naming-panel__tab--disabled': !categoryPurchaseMap[c.key],
          }"
          :disabled="!categoryPurchaseMap[c.key]"
          @click="onClickCategoryTab(c.key)"
        >
          {{ c.label }}
        </button>
      </div>
      <div ref="historyListRef" class="naming-panel__history-list">
        <PurchaseHistoryCard
          v-for="item in filteredPurchaseList"
          :key="item.id"
          :saju-name="item.sajuName"
          :selection-info="formatNamingPurchaseLabel(item.modeKey, item.detailInput)"
          :purchase-date="item.purchaseDate"
          :valid-until="item.validUntil"
          :active="activeLogId != null && String(item.analysisLogId) === String(activeLogId)"
          @click="onClickPurchaseCard(item)"
        />
        <div v-if="!filteredPurchaseList.length" class="naming-panel__history-empty">
          {{ guestUserId ? '현재 사주의 구매 내역이 없습니다.' : '구매 내역이 없습니다.' }}
        </div>
      </div>
    </div>

    <button
      type="button"
      class="naming-panel__cta"
      :class="{ disabled: isCtaDisabled }"
      :disabled="isCtaDisabled"
      @click="onClickCta"
    >
      <template v-if="isDetailMode">
        <svg
          class="naming-panel__cta-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.65 6.35A7.958 7.958 0 0 0 12 4a8 8 0 1 0 7.74 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
            fill="currentColor"
          />
        </svg>
        새 이름 짓기
      </template>
      <template v-else>
        낭만작명 시작하기
        <span class="naming-panel__cta-arrow">→</span>
      </template>
    </button>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/nangman_style/_analysis_main_color.scss';

.naming-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px 28px 28px;
  width: 100%;
  height: 100%;
  background: #fff;
  box-sizing: border-box;
}

.naming-panel__ticket {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #ebebeb;
  background: #fff;

  &-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #333;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  &-empty {
    color: #6c6c6c;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  &-count {
    color: #333;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
  }
}

.ticket-icon__analysis {
  display: inline-block;
  width: 20px;
  height: 20px;
  background: url('@manseImages/ticket-icon.svg') center center no-repeat;
  background-size: contain;
  vertical-align: middle;
  flex-shrink: 0;
}

.naming-panel__hero {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background: $analysis-naming-bg;

  &-title {
    margin: 0;
    color: $analysis-naming;
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
  }

  &-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 10px;
    background: #fff;

    p {
      margin: 0;
      color: #4c4c4c;
      font-family: Pretendard;
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
    }
  }
}

.naming-panel__history {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* 남은 세로 공간을 모두 차지 — 카드가 넘치면 list 영역이 내부 스크롤된다.
     min-height: 0 은 flex 자식이 컨텐츠 높이 이하로 줄어들 수 있도록 필요 */
  flex: 1;
  min-height: 0;
}

.naming-panel__history-title {
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.naming-panel__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.naming-panel__tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 15px;
  border-radius: 100px;
  border: 1px solid transparent;
  background: #f8f8f8;
  color: #757575;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  white-space: nowrap;
  box-sizing: border-box;

  &:hover {
    background: #f0f0f0;
  }

  &.active {
    background: #fff8f5;
    border-color: #ff8800;
    color: #eb4c10;
    font-weight: 700;
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: #f8f8f8;
    }
  }
}

.naming-panel__history-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* 카드가 panel 가용 공간을 넘으면 list 내부에서만 스크롤 (CTA는 하단 고정) */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* 스크롤바를 카드에서 띄우기 — 카드 폭은 유지하고 스크롤바만 우측 패딩 영역으로 분리 */
  padding-right: 8px;
  margin-right: -8px;
}

.naming-panel__history-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 93px;
  border-radius: 12px;
  border: 1px solid #ebebeb;
  background: #fff;
  color: #6c6c6c;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
}

.naming-panel__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 14px 20px;
  border-radius: 6px;
  border: none;
  background: #eb4c10;
  color: #fff;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;

  &.disabled {
    background: #949494;
    cursor: not-allowed;
  }

  &-arrow {
    font-size: 15px;
    line-height: 20px;
  }

  &-icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
}
</style>
